import React, { useMemo } from 'react'
import { Button, Select, Tag, Tooltip } from 'antd'
import { SwapOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { IdeonVersion } from '../api/ideon-compare-api'
import {
  formatVersionDate,
  groupVersionsByYear,
  versionsForYear,
  type IdeonCompareSelection,
} from '../utils/ideonCompareHelpers'
import styles from '../PlanManagementV3.module.scss'

function VersionOption({
  version,
  isLatest,
}: {
  version: IdeonVersion
  isLatest: boolean
}) {
  return (
    <span className={styles.ideonVersionOption}>
      <span className={styles.ideonVersionDate}>
        {formatVersionDate(version)}
      </span>
      <span className={styles.ideonVersionMeta}>by {version.fetchedBy}</span>
      {isLatest && (
        <Tag color="cyan" className={styles.ideonLatestTag}>
          Latest
        </Tag>
      )}
    </span>
  )
}

export function IdeonVersionSelector({
  versions,
  isLoadingVersions,
  selection,
  isRunning,
  onChange,
  onCompare,
}: {
  versions: IdeonVersion[]
  isLoadingVersions: boolean
  selection: IdeonCompareSelection
  isRunning: boolean
  onChange: (next: IdeonCompareSelection) => void
  onCompare: () => void
}) {
  const yearGroups = useMemo(() => groupVersionsByYear(versions), [versions])
  const yearVersions = versionsForYear(yearGroups, selection.planYear)
  const latestId = yearVersions[0]?.id

  const versionOptions = (excludeId: string | null) =>
    yearVersions
      .filter((v) => v.id !== excludeId)
      .map((v) => ({
        value: v.id,
        label: <VersionOption version={v} isLatest={v.id === latestId} />,
      }))

  const base = yearVersions.find((v) => v.id === selection.baseVersionId)
  const target = yearVersions.find((v) => v.id === selection.targetVersionId)
  const isReversed =
    !!base && !!target && dayjs(base.fetchedAt).isAfter(target.fetchedAt)
  const hasTooFewVersions = yearVersions.length < 2
  const canSubmit = !!base && !!target && !isRunning && !hasTooFewVersions

  const handleYearChange = (planYear: number) => {
    const list = versionsForYear(yearGroups, planYear)
    onChange({
      ...selection,
      planYear,
      baseVersionId: list[1]?.id ?? null,
      targetVersionId: list[0]?.id ?? null,
    })
  }

  const handleSwap = () =>
    onChange({
      ...selection,
      baseVersionId: selection.targetVersionId,
      targetVersionId: selection.baseVersionId,
    })

  const compareButton = (
    <Button
      type="primary"
      className={styles.uploadAnalyzeButton}
      disabled={!canSubmit}
      loading={isRunning}
      onClick={onCompare}
    >
      {isRunning ? 'Comparing…' : 'Compare'}
    </Button>
  )

  return (
    <div className={styles.ideonSelectorCard}>
      <div className={styles.ideonSelectorRow}>
        <div className={`${styles.ideonField} ${styles.ideonYearField}`}>
          <span className={styles.ideonFieldLabel}>Plan year</span>
          <Select
            aria-label="Plan year"
            value={selection.planYear ?? undefined}
            loading={isLoadingVersions}
            disabled={isRunning}
            onChange={handleYearChange}
            options={yearGroups.map(({ planYear, versions: list }) => ({
              value: planYear,
              disabled: list.length < 2,
              label: (
                <span className={styles.ideonYearOption}>
                  {planYear}
                  <span className={styles.ideonVersionMeta}>
                    {list.length} {list.length === 1 ? 'fetch' : 'fetches'}
                  </span>
                </span>
              ),
            }))}
          />
        </div>

        <div className={styles.ideonField}>
          <span className={styles.ideonFieldLabel}>Base version</span>
          <Select
            aria-label="Base version"
            placeholder="Select a fetch"
            value={selection.baseVersionId ?? undefined}
            disabled={isRunning || hasTooFewVersions}
            onChange={(baseVersionId: string) =>
              onChange({ ...selection, baseVersionId })
            }
            options={versionOptions(selection.targetVersionId)}
          />
        </div>

        <Tooltip title="Swap versions">
          <Button
            aria-label="Swap versions"
            className={styles.ideonSwapButton}
            icon={<SwapOutlined />}
            disabled={isRunning || !base || !target}
            onClick={handleSwap}
          />
        </Tooltip>

        <div className={styles.ideonField}>
          <span className={styles.ideonFieldLabel}>Compare to</span>
          <Select
            aria-label="Compare to version"
            placeholder="Select a fetch"
            value={selection.targetVersionId ?? undefined}
            disabled={isRunning || hasTooFewVersions}
            onChange={(targetVersionId: string) =>
              onChange({ ...selection, targetVersionId })
            }
            options={versionOptions(selection.baseVersionId)}
          />
        </div>

        <div className={styles.ideonSelectorActions}>{compareButton}</div>
      </div>

      {hasTooFewVersions && selection.planYear !== null && (
        <p className={styles.ideonSelectorHint}>
          Plan year {selection.planYear} needs at least 2 successful fetches to
          compare.
        </p>
      )}
      {isReversed && (
        <p className={styles.ideonSelectorHint}>
          The base version is newer than the one you&apos;re comparing to, so
          Added and Removed will be reversed.{' '}
          <Button type="link" size="small" onClick={handleSwap}>
            Swap versions
          </Button>
        </p>
      )}
    </div>
  )
}
